import * as React from 'react';
import { toast } from 'react-toastify';
import type { CustomerDetails, CustomerContacts, CustomerPriceListItem, CustomerRules } from './models/CustomerInterfaces';
import { useNavigate, useParams } from 'react-router-dom';
import CustomerDetail from './CustomerDetails';
import CustomerContact from './CustomerContacts';
import CustomerPricelist from './CustomerPricing';
import CustomerRule from './CustomerRules';
import { GetQuery, GetSystemCompanyId, PostQuery } from './Shared';

interface validationCheck {
    isValid: boolean;
    detailError: boolean;
    contactError: boolean;
    pricingError: boolean;
    rulesError: boolean;
}

type CustomerRouteParams = {
    id: string;
};

const initCustomerDetails: CustomerDetails = {
    id: 0,
    name: "",
    internalName: "",
    abn: "",
    acn: "",
    streetName: "",
    streetNameTwo: "",
    suburb: "",
    state: "",
    country: "",
    postCode: "",
    gstRegistered: -1,
    accountTypeId: -1,
    accountLimit: 0,
    accountLimitDunny: 0,
    phone: "",
    invoiceEmail: "",
    statusId: 1,
    lockAccount: false,
    terms: -1,
    statusTypeId: -1,
    statusType: "",
    existingStatusTypeId: -1,
    isLinkedToReckonScaffold: false,
    isLinkedToReckonDunny: false,
    accountId: "",
    accountIdDunny: "",
    createScaffoldAccount: false,
    scaffoldNotUsed: false,
    createDunnyAccount: false,
    dunnyNotUsed: false,
    accountContact: "",
    quoteNotesDunny: "",
    quoteNotes: "",
    priceListTierId: -1,
    calloutFee: 0,
    fencePartPickupFee: 0,
    fuelSurcharge: 0,
    fuelSurchargePumpClean: 0
}

const CustomerData = () => {
    let navigate = useNavigate();
    const { id } = useParams<CustomerRouteParams>();
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [tab, setTab] = React.useState("Details");
    const [reckonSwitchedon, setReckonSwitchedOn] = React.useState(false);
    const [hasCustomPriceList, setHasCustomPriceList] = React.useState(false);
    const [systemCompanyId, setSystemCompanyId] = React.useState(2);
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
    const [readOnly, setReadOnly] = React.useState(false);
    const [editNoReckonAccess, setEditNoReckonAccess] = React.useState(false);
    const initCustomerContact: CustomerContacts = {
        id: 0,
        firstName: "",
        lastName: "",
        phone: "",
        mobile: "",
        email: "",
        emailSubscription: true,
        statusId: 1
    };
    const initCustomerRule: CustomerRules = {
        id: 0,
        companyId: 2,
        allowOnGoingHire: false,
        createScaffoldAudit: false,
        requireOverhireNewPo: false,
        invoicePeriod: 0,
        invoicePeriodVoid: 0,
        publicHolidayCharge: true,
        shutdownPeriodType: 1,
        overhireRateDiscountPercentage: 0,
        splitInvoiceByPo: false,
        gateCode: "",
        sendConfirmationEmail: true,
        invoiceToBePrinted: false,
        eventInvoiceOnDelivery: false,
        invoiceQuotedAmount: false,
        epaTransportCertificateCheck: false,
        chargeFuelSurcharge: true,
        fuelSurchargeNewInvoice: false
    };
    const initCustomerPricelist: CustomerPriceListItem = {
        id: 0,
        tierId: -1,
        customerId: -1,
        customerName: "",
        itemId: -1,
        itemTypeId: 0,
        hirePeriod: 0,
        frequencyId: -1,
        locationId: -1,
        metreId: -1,
        baseDays: 0,
        basePrice: 0,
        baseMetres: 0,
        useBtm: false,
        metreRate: 0,
        travelAmount: 0,
        baseAdditionalHireRate: 0,
        additionalHireRate: 0,
        extraHourRate: 0,
        overhireRequired: true,
        statusId: 1,
        isDirty: false
    }
    const [customerDetails, setCustomerDetails] = React.useState<CustomerDetails>(initCustomerDetails);
    const [customerContacts, setCustomerContacts] = React.useState<CustomerContacts[]>([initCustomerContact]);
    const [customPriceList, setCustomPriceList] = React.useState<CustomerPriceListItem[]>([initCustomerPricelist]);
    const [customerRule, setCustomerRule] = React.useState<CustomerRules>(initCustomerRule);
    
    React.useEffect(() => {
        window.scrollTo(0, 0);

        getData();
    }, []);

    const getData = async () => {
        const apiUrl = "CheckCustomerUserPermission";
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            if (result.data.canEdit || result.data.canEditNoReckonAccess || result.data.canView) {
                var readOnlyPermission = !result.data.canEdit && !result.data.canEditNoReckonAccess && result.data.canView;
                setReadOnly(readOnlyPermission);
                setEditNoReckonAccess(result.data.canEditNoReckonAccess);

                if (result.data.canEditNoReckonAccess || readOnlyPermission) {
                    //get customer details without reckon
                    getCustomerDetails();
                } else {
                    //check logged into reckon
                    checkReckonLogin();
                }
            } else {
                //redirect to 403 permission denied
                navigate("/accessdenied");
            }
        }
    }

    const checkReckonLogin = async () => {
        //enter the page that quickbooks needs to return to, id blank as not used via invoicing
        var page = {
            page: "customer",
            id: id
        };
        //encode to base 64 so can retrieve it later
        var encoded = window.btoa(JSON.stringify(page));

        const apiUrl = "ReckonCustomerAuthoriseCheck?returnUrl=" + encoded;
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            //continue if Quick Books user is authorised or if Quick Books is switched off
            setReckonSwitchedOn(result.data.reckonSwitchedOn);
            if (result.data.isAuthorised || !result.data.reckonSwitchedOn) {
                getCustomerDetails();
            } else {
                navigate(result.data.reckonLoginUrl);
            }
        }
    }

    const getCustomerDetails = async () => {
        //get customer details
        if (Number(id) > 0) {
            const apiUrl = 'GetCustomerDetails?customerId=' + id;
            var result = await GetQuery(apiUrl);

            if (result.error) {
                toast.error("Error getting data!");
                setLoading(false);
            } else {
                var blankCustContact: CustomerContacts = {
                    id: 0,
                    firstName: "",
                    lastName: "",
                    phone: "",
                    mobile: "",
                    email: "",
                    emailSubscription: true,
                    statusId: 1
                };
                var custContacts = result.data.customerContacts;
                custContacts.push(blankCustContact);

                var customNewRow = addBlankCustomPricelistRow();
                var custPricelist = result.data.customPriceList;
                custPricelist.push(customNewRow);

                setSystemCompanyId(result.data.systemCompanyId);
                setCustomerDetails(result.data.customerDetails);
                setCustomerContacts(custContacts);
                setCustomPriceList(custPricelist);
                setHasCustomPriceList(result.data.hasCustomPriceList);
                setCustomerRule(result.data.customerRule);
                setLoading(false);
            }
        }
    }

    const addBlankCustomPricelistRow = () => {
        let pricelistItem: CustomerPriceListItem = {
            id: 0,
            tierId: -1,
            customerId: customerDetails.id,
            customerName: customerDetails.name,
            itemId: -1,
            itemTypeId: 0,
            hirePeriod: 0,
            frequencyId: -1,
            locationId: -1,
            metreId: -1,
            baseDays: 0,
            basePrice: 0,
            baseMetres: 0,
            useBtm: false,
            metreRate: 0,
            travelAmount: 0,
            baseAdditionalHireRate: 0,
            additionalHireRate: 0,
            extraHourRate: 0,
            overhireRequired: true,
            statusId: 1,
            isDirty: false
        };
        return pricelistItem;
    }

    const tabSelected = (newTab: string) => {
        setTab(newTab);
    }

    const tabChanged = (e: any) => {
        tabSelected(e.target.value);
    }

    const noChange = (e: any) => {
        e.preventDefault();
        //no changes as user doesn't actually click on the radio button
    }

    const updateAccountType = (newAcctTypeId: number) => {
        let splitInvoice = newAcctTypeId === 3;
        setCustomerRule(prevState => ({ ...prevState, splitInvoiceByPo: splitInvoice }));

        setCustomerDetails(prevState => ({ ...prevState, accountTypeId: newAcctTypeId }));
    }

    const updateDetailsString = (fieldName: string, fieldValue: string) => {
        setCustomerDetails(prevState => ({ ...prevState, [fieldName]: fieldValue }));
    }

    const updateDetailsNumber = (fieldName: string, fieldValue: number) => {
        setCustomerDetails(prevState => ({ ...prevState, [fieldName]: fieldValue }));
    }

    const updateDetailsChecked = (fieldName: string, fieldValue: boolean, unsetFieldName: string) => {
        if (unsetFieldName != "") {
            setCustomerDetails(prevState => ({ ...prevState, [fieldName]: fieldValue, [unsetFieldName]: false }));
        } else {
            setCustomerDetails(prevState => ({ ...prevState, [fieldName]: fieldValue }));
        }
    }

    const updateDetails = (customer: CustomerDetails) => {
        setCustomerDetails(prevState => ({ ...prevState, customer }));
    }

    const updateContacts = (custContacts: CustomerContacts[]) => {
        setCustomerContacts(custContacts);
    }

    const updatePriceListTierId = (fieldName: string, fieldValue: number) => {
        setCustomerDetails(prevState => ({ ...prevState, [fieldName]: fieldValue }));
    }

    const updateCustomPricelist = (custPriceList: CustomerPriceListItem[]) => {
        setCustomPriceList(custPriceList);
    }

    const updateErrors = (updatedErrors:any) => {
        setErrors(prevState => ({ ...prevState, updatedErrors }));
    }

    const updateQuoteNotes = (scaffoldQuoteNotes: string, dunnyQuoteNotes: string) => {
        setCustomerDetails(prevState => ({ ...prevState, quoteNotes: scaffoldQuoteNotes, quoteNotesDunny: dunnyQuoteNotes }));
    }

    const updateRulesString = (fieldName: string, fieldValue: string) => {
        setCustomerRule(prevState => ({ ...prevState, [fieldName]: fieldValue }));
    }

    const updateRulesNumber = (fieldName: string, fieldValue: number) => {
        setCustomerRule(prevState => ({ ...prevState, [fieldName]: fieldValue }));
    }

    const updateRulesChecked = (fieldName: string, fieldValue: boolean) => {
        setCustomerRule(prevState => ({ ...prevState, [fieldName]: fieldValue }));
    }

    const validate = () => {
        let errorList: { [key: string]: string } = {};
        let formIsValid = true;
        let detailsError = false;

        var errorString = "Please enter the following values: ";
        
        let custDetails = customerDetails;
        if (custDetails.statusTypeId < 0) {
            formIsValid = false;
            detailsError = true;
            errorList["statusTypeId"] = "Status Type is required";
        } else if (custDetails.statusTypeId == 1) {
            //quote only customers only require Name, Account Type and Status
            if (!custDetails.name) {
                formIsValid = false;
                detailsError = true;
                errorList["name"] = "Name is required";
            }
            if (!custDetails.internalName) {
                formIsValid = false;
                detailsError = true;
                errorList["internalName"] = "Reckon Name is required";
            }
            if (custDetails.accountTypeId < 1) {
                formIsValid = false;
                detailsError = true;
                errorList["accountTypeId"] = "Account Type is required";
            }
            if (custDetails.statusId < 0) {
                formIsValid = false;
                detailsError = true;
                errorList["statusId"] = "Status is required";
            }
        } else {
            if (!custDetails.name) {
                formIsValid = false;
                detailsError = true;
                errorList["name"] = "Name is required";
            }
            if (!custDetails.internalName) {
                formIsValid = false;
                detailsError = true;
                errorList["internalName"] = "Reckon Name is required";
            }

            if (!custDetails.streetName) {
                formIsValid = false;
                detailsError = true;
                errorList["streetName"] = "Address 1 is required";
            }
            if (!custDetails.suburb) {
                formIsValid = false;
                detailsError = true;
                errorList["suburb"] = "Suburb is required";
            }
            if (!custDetails.state) {
                formIsValid = false;
                detailsError = true;
                errorList["state"] = "State is required";
            }

            if (custDetails.gstRegistered < 0) {
                formIsValid = false;
                detailsError = true;
                errorList["gstRegistered"] = "Registered for GST is required";
            } else {
                if (custDetails.createDunnyAccount || custDetails.createScaffoldAccount) {
                    if (custDetails.gstRegistered == 1 && !custDetails.abn) {
                        formIsValid = false;
                        detailsError = true;
                        errorList["abn"] = "ABN is required";
                    }
                }
            }

            if (custDetails.accountTypeId < 1) {
                formIsValid = false;
                detailsError = true;
                errorList["accountTypeId"] = "Account Type is required";
            } else {
                if (custDetails.accountTypeId == 2 || custDetails.accountTypeId == 3) {
                    if ((systemCompanyId === 2 || custDetails.id === 0) && custDetails.accountLimit < 1) {
                        formIsValid = false;
                        detailsError = true;
                        errorList["accountLimit"] = "Account Limit is required";
                    }
                    if ((systemCompanyId === 3 || custDetails.id === 0) && custDetails.accountLimitDunny < 1) {
                        formIsValid = false;
                        detailsError = true;
                        errorList["accountLimitDunny"] = "Account Limit is required";
                    }
                }
            }
            if (!custDetails.phone) {
                formIsValid = false;
                detailsError = true;
                errorList["phone"] = "Phone is required";
            }
            if (!custDetails.invoiceEmail) {
                formIsValid = false;
                detailsError = true;
                errorList["invoiceEmail"] = "Invoice Email is required";
            }
            if (custDetails.terms < 0) {
                formIsValid = false;
                detailsError = true;
                errorList["terms"] = "Account Terms are required";
            }
            if (custDetails.statusId < 0) {
                formIsValid = false;
                detailsError = true;
                errorList["statusId"] = "Status is required";
            }
        }

        //Validate Customer Contacts
        let custContacts = customerContacts;
        var contactError = false;
        
        //last row is a blank new row so ignore
        var rowNumber = 0;
        for (var i = 0; i < custContacts.length - 1; i++) {
            rowNumber += 1;
            var newErrorString = "";
            if (!custContacts[i].firstName) {
                newErrorString += " First Name, ";
                contactError = true;
            }
            if (!custContacts[i].phone && !custContacts[i].mobile) {
                newErrorString += " Phone or Mobile, ";
                contactError = true;
            }
            if (newErrorString != "") {
                errorString += "\n"
                errorString += "Row " + rowNumber + ": " + newErrorString;
                errorString = errorString.substring(0, errorString.length - 2);
            }
        }
        if (contactError) {
            formIsValid = false;
            errorList["contacts"] = errorString;
        }

        //Validate Customer Pricing
        let priceError = false;
        if (hasCustomPriceList) {
            var pricingCustomError = validateCustom();
            if (pricingCustomError != "") {
                errorList["pricelistitems"] = pricingCustomError;
                priceError = true;
            }
        }

        //Validate Customer Rules
        var custError = false;
        var custRules = customerRule;
        if (custRules.allowOnGoingHire) {
            if (custRules.invoicePeriod < 1) {
                formIsValid = false;
                custError = true;
                errorList["invoicePeriod"] = "Invoice Period must be > 0 days";
            }
        }

        if (custRules.shutdownPeriodType < 0) {
            formIsValid = false;
            custError = true;
            errorList["shutdownPeriodType"] = "Shutdown Period Charge is required";
        }

        setErrors(errorList);
        
        var valid: validationCheck = {
            isValid: formIsValid,
            detailError: detailsError,
            contactError: contactError,
            pricingError: priceError,
            rulesError: custError,
        }

        return valid;
    }

    const validateCustom = () => {
        var pricingError = "";
        let items = customPriceList;

        if (items.length === 1) {
            return "Please enter at least one item!";
        }

        var rowNumber = 0;
        var errorString = "Please enter the following values: ";
        var error = false;

        for (var i = 0; i < items.length - 1; i++) {
            rowNumber += 1;
            var newErrorString = "";

            if (items[i].itemId < 1) {
                newErrorString += "Item, ";
                error = true;
            }

            if (!items[i].basePrice || items[i].basePrice <= 0) {
                newErrorString += "Base Price, ";
                error = true;
            }

            if (!items[i].additionalHireRate || items[i].additionalHireRate <= 0) {
                newErrorString += "Additional Hire Rate, ";
                error = true;
            }

            if (systemCompanyId === 2 && (!items[i].extraHourRate || items[i].extraHourRate <= 0)) {
                newErrorString += "Extra Hour Rate, ";
                error = true;
            }

            if (newErrorString != "") {
                errorString += "\n"
                errorString += "Row " + rowNumber + ": " + newErrorString;
                errorString = errorString.substring(0, errorString.length - 2);
            }

        }
        if (error) {
            pricingError = errorString;
        }

        return pricingError;
    }

    const save = () => {
        let valid = validate();
        if (valid.isValid) {
            saveCustomer();
        } else {
            toast.error("Please fix errors on the following:");
            if (valid.detailError) {
                toast.error("Details Screen");
            }
            if (valid.contactError) {
                toast.error("Contacts Screen");
            }
            if (valid.pricingError) {
                toast.error("Pricing Screen");
            }
            if (valid.rulesError) {
                toast.error("Rules Screen");
            }
        }
    }

    const saveCustomer = async () => {
        setSaving(true);

        //remove blank row from item list
        let custContacts = [...customerContacts];
        custContacts.pop();

        //just add/update any prices that have changed
        var custPricelist = customPriceList.filter(pl => pl.isDirty);

        var customerToSave = {
            customerDetails: customerDetails,
            customerContacts: custContacts,
            customPriceList: custPricelist,
            customerRule: customerRule
        };

        var jsonItem = JSON.stringify(customerToSave);

        const apiUrl = "SaveCustomer";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            if (result.data.isError) {
                var errorList = result.data.messages as any[];
                errorList.map(function (error: any) {
                    toast.error(error.content);
                });
            } else {
                if (result.data.quickBooksError) {
                    toast.error("Dash Customer Saved. Reckon Customer Error:" + result.data.quickBooksErrorMessage);
                    setCustomerDetails(prevState => ({ ...prevState, id: result.data.id }));
                } else {
                    //if it was a new customer then redirect back to customer search else stay in this screen
                    getCustomerDetails();
                    toast.success("Customer Saved");
                }
            }
            setSaving(false);
        }
    }

    var contents;
    if (tab == "Details") {
        contents = <CustomerDetail id={customerDetails.id} systemCompanyId={systemCompanyId} customerDetails={customerDetails} reckonSwitchedon={reckonSwitchedon} readOnly={readOnly || editNoReckonAccess} errors={errors} updateAccountType={updateAccountType} updateDetailsString={updateDetailsString} updateDetailsNumber={updateDetailsNumber} updateDetailsChecked={updateDetailsChecked} updateCustomer={updateDetails} updateErrors={updateErrors} />
    }
    else if (tab == "Contacts") {
        contents = <CustomerContact id={customerDetails.id} readOnly={readOnly} customerContacts={customerContacts} accountContactName={customerDetails.accountContact} accountContactPhone={customerDetails.phone} accountContactEmail={customerDetails.invoiceEmail} errors={errors} updateContacts={updateContacts} />
    }
    else if (tab == "Pricing") {
        contents = <CustomerPricelist id={customerDetails.id} readOnly={readOnly || editNoReckonAccess} priceListTierId={customerDetails.priceListTierId} calloutFee={customerDetails.calloutFee} fencePartPickupFee={customerDetails.fencePartPickupFee} fuelSurcharge={customerDetails.fuelSurcharge} fuelSurchargePumpClean={customerDetails.fuelSurchargePumpClean} customPriceList={customPriceList} hasCustomPriceList={hasCustomPriceList} scaffoldQuoteNotes={customerDetails.quoteNotes} dunnyQuoteNotes={customerDetails.quoteNotesDunny} errors={errors} updatePriceListTierId={updatePriceListTierId} updateCustom={updateCustomPricelist} updateQuoteNotes={updateQuoteNotes} updateErrors={updateErrors} />
    }
    else if (tab == "Rules") {
        contents = <CustomerRule id={customerDetails.id} readOnly={readOnly || editNoReckonAccess} accountTypeId={customerDetails.accountTypeId} customerRule={customerRule} systemCompanyId={systemCompanyId} errors={errors} updateRulesString={updateRulesString} updateRulesNumber={updateRulesNumber} updateRulesChecked={updateRulesChecked} updateErrors={updateErrors} />
    }

    const renderCustomer = (
        <div>
            <div className="tabsComponent" id="tab-container">

                <div className="tabsComponent__showDropdown">
                    <label className="input-group" htmlFor="userTab">
                        <span className="label">Item Type</span>
                        <select className="select" id="userTab" value={tab} onChange={tabChanged} >
                            <option value="Details">Details</option>
                            <option value="Contacts">Contacts</option>
                            <option value="Pricing">Pricing</option>
                            <option value="Rules">Rules</option>
                        </select>
                    </label>
                </div>
                <div className="tabsComponent__tabs tabsComponent__hideTabs">
                    <input type="radio" name="tabs" id="tab1" className="tabsComponent__control" checked={tab == 'Details'} onChange={(e) => noChange(e)}></input>
                    <label className="tabsComponent__label" htmlFor="tabItem" onClick={() => tabSelected('Details')}>Details</label>
                    <input type="radio" name="tabs" id="tab2" className="tabsComponent__control" checked={tab == 'Contacts'} onChange={(e) => noChange(e)}></input>
                    <label className="tabsComponent__label" htmlFor="tabItem" onClick={() => tabSelected('Contacts')}>Contacts</label>
                    <input type="radio" name="tabs" id="tab3" className="tabsComponent__control" checked={tab == 'Pricing'} onChange={(e) => noChange(e)}></input>
                    <label className="tabsComponent__label" htmlFor="tabItem" onClick={() => tabSelected('Pricing')}>Pricing</label>
                    <input type="radio" name="tabs" id="tab4" className="tabsComponent__control" checked={tab == 'Rules'} onChange={(e) => noChange(e)}></input>
                    <label className="tabsComponent__label" htmlFor="tabItem" onClick={() => tabSelected('Rules')}>Rules</label>
                </div>

                <div className="tabsComponent__content tabsComponent__content--selected">
                    <div>
                        {contents}
                    </div>

                    <button className={tab == "Contacts" ? "defaultbutton" : "defaultbutton marginLeftLabelLarge"} type="button" onClick={save} disabled={saving || readOnly}>Save</button>
                    <div className={saving ? tab == "Contacts" ? "" : "marginLeftLabelLarge" : "hidden"}>Saving...</div>
                </div>

            </div>

        </div>
    );

    let contentsFinal = loading
        ? <p><em>Loading...</em></p>
        : renderCustomer;


    return <div>
        <h1>Customer</h1>
        {contentsFinal}
    </div>;
}

export default CustomerData;

